// Shared pt-BR vocabulary for the Diretoria readiness ("pre-flight") payload
// (`GET /director/readiness?company_id=`). Consumed today by the backoffice
// pre-flight checklist (`ProductAccess.jsx`); safe to import from the director
// module (`outlets/director/*`) too, since it has no backoffice-only dependency.

// Same labels the "Permissões Sienge" screen already uses for these two scopes
// (`SiengePermissions.jsx` catalog, `checkSiengePermissions.js`/`probeSiengeScopes.py`).
export const SCOPE_LABELS = Object.freeze({
  bulk_income: "Bulk — Parcelas a Receber",
  bulk_outcome: "Bulk — Parcelas a Pagar",
});

export const REASON_TEXTS = Object.freeze({
  entitlement_inactive: "O contrato da Diretoria não está ativo no momento.",
  portfolio_missing: "Portfólio não configurado — monte o mapeamento técnico no backoffice antes de habilitar.",
  portfolio_ambiguous: "Há mais de um portfólio ativo para esta empresa — corrija antes de habilitar.",
  portfolio_unmapped_projects: "Há obra(s) contratada(s) sem mapeamento técnico no portfólio.",
  scopes_missing: "Faltam escopos da API Sienge liberados pelo cliente.",
  scopes_stale: "A verificação de permissões Sienge está desatualizada (mais de 7 dias) — reverifique.",
  quota_insufficient: "A cota diária da API Sienge não é suficiente para esta atualização.",
  director_mode_disabled: "A atualização da Diretoria está desligada globalmente (freio de emergência).",
});

// Expands each reason code into a full sentence, appending the specific missing
// scopes / unmapped project names the pre-flight already returned, so the admin
// sees exactly what to fix instead of a generic code.
export const describeReadinessReasons = (readiness) => {
  const reasons = Array.isArray(readiness?.reasons) ? readiness.reasons : [];
  return reasons.map((code) => {
    if (code === "portfolio_unmapped_projects") {
      const names = (readiness?.unmapped_contracted_projects ?? [])
        .map((project) => project?.name)
        .filter(Boolean);
      return names.length > 0
        ? `${REASON_TEXTS[code]} Sem mapeamento: ${names.join(", ")}.`
        : REASON_TEXTS[code];
    }
    if (code === "scopes_missing") {
      const missing = (readiness?.missing_scopes ?? [])
        .map((scope) => SCOPE_LABELS[scope] ?? scope);
      return missing.length > 0
        ? `${REASON_TEXTS[code]} Faltando: ${missing.join(", ")}.`
        : REASON_TEXTS[code];
    }
    return REASON_TEXTS[code] ?? code;
  });
};

// Yellow-warning text for a quota that is not (yet) insufficient but close to it
// (`quota_warning`, expected > 70% of the remaining quota — §4.6 of the spec).
export const describeQuotaWarning = (readiness) => {
  if (!readiness || !readiness.quota_warning) return null;
  return `Cota apertada hoje: restam ${readiness.remaining_quota_today} de ${readiness.api_limit_bulk} chamadas, e esta atualização deve usar cerca de ${readiness.expected_request_count}.`;
};

export default {
  SCOPE_LABELS,
  REASON_TEXTS,
  describeReadinessReasons,
  describeQuotaWarning,
};
